/**
 * The assistance ladder and the independence rule.
 *
 * INVARIANT 1: a response is never independent if answer-revealing help
 * preceded it. This module is the only place that decides independence.
 * Nothing else in the system may set it, and no client may submit it.
 *
 * **The invariant is the floor, not the whole rule (D15, #67).** Answer-
 * revealing help always costs independence and always will. Since D15 the
 * operative rule is stricter: independence survives only help that
 * *re-presents the task*, and is lost the moment help *supplies information
 * toward the answer*. Rungs 2-4 cost independence under that rule and did not
 * before. See `AssistanceKind` below for why the line is drawn there and not
 * at a language boundary.
 *
 * **Where the work was produced is an input too (D31, #380).** Independence is
 * a claim about what the learner did *under observation* (D11). Work uploaded
 * from outside DaVinci — homework, anyone's — was not observed being produced,
 * so no amount of "no help was delivered" can make it independent. That is
 * decided here, from the response's observed channel, and nowhere else.
 *
 * This file has no imports on purpose. It must stay pure so the invariant
 * can be tested without a database, a server, or a model.
 */

/** Rungs 0-5 of the assistance ladder. Defined in BACKEND_DESIGN §6. */
export const ASSISTANCE_LEVELS = [0, 1, 2, 3, 4, 5] as const;

export type AssistanceLevel = (typeof ASSISTANCE_LEVELS)[number];

/**
 * What a rung *does to the evidence*, as opposed to what it contains.
 *
 * The kinds exist because the first attempt at this rule was drawn at a
 * language boundary — help inside Italian preserved independence, help in the
 * learner's support language did not. That line is wrong for a reason worth
 * recording: DaVinci is not an Italian program. It is an instrument that
 * happens to be pointed at an Italian textbook first, and a rule phrased in
 * terms of Italian cannot judge the second subject it is pointed at.
 *
 * So each rung declares a kind, and the independence rule reads the kind:
 *
 * - `none` — nothing was delivered.
 * - `re_presentation` — the task was restated, repeated, or simplified. The
 *   learner receives nothing they did not already have; only the packaging
 *   changed. A learner who asks for the prompt again has not been helped
 *   toward the answer, and charging them independence for asking is what
 *   makes the help button costly to press (HYP-UX-002).
 * - `informative` — the help supplied information the learner did not have,
 *   short of the answer itself. A cue, a gloss, an explanation.
 * - `revealing` — the answer, a worked example, or anything from which the
 *   answer follows directly.
 *
 * Nothing here names a language, a subject, or a medium. A future ladder for
 * a different domain declares the same four kinds over its own rungs, and the
 * independence rule needs no change to judge it.
 */
export const ASSISTANCE_KINDS = Object.freeze([
  "none",
  "re_presentation",
  "informative",
  "revealing",
] as const);

export type AssistanceKind = (typeof ASSISTANCE_KINDS)[number];

/**
 * How much of the answer each kind carries, as an order.
 *
 * Used to combine a rung's declared kind with what a delivery turned out to
 * be: the more severe of the two wins, never the less severe. This function
 * is allowed to be over-cautious and is never allowed to be over-generous.
 */
const KIND_SEVERITY: Readonly<Record<AssistanceKind, number>> = Object.freeze({
  none: 0,
  re_presentation: 1,
  informative: 2,
  revealing: 3,
});

/**
 * The kinds a response can survive and still be independent.
 *
 * **Frozen, not merely `as const`** — `as const` is a compile-time claim and
 * this array is read live on every `costsIndependence` call, so an in-process
 * `push('informative')` would restore pre-D15 behaviour at rungs 2-4 silently
 * and permanently. Found by `invariant-auditor` on the D15 diff, which noted
 * that `ASSISTANCE_LADDER` below was frozen twice over while the constant that
 * actually gates the decision was not.
 */
export const INDEPENDENCE_PRESERVING_KINDS: readonly AssistanceKind[] =
  Object.freeze(["none", "re_presentation"] as const);

export function costsIndependence(kind: AssistanceKind): boolean {
  return !INDEPENDENCE_PRESERVING_KINDS.includes(kind);
}

/**
 * The version of the ladder and of the rule that reads it.
 *
 * **`@1` is the unversioned pre-D15 ladder**, under which rungs 1-4 all
 * preserved independence. It is named here rather than left implicit because
 * invariant 3 requires a conclusion to name the policy that produced it, and
 * a row written under the old rule must not be mistaken for one written under
 * this one. Any change to a rung's `kind`, or to
 * `INDEPENDENCE_PRESERVING_KINDS`, is a new version — it changes what
 * `can_use_independently` means, which is a claim about the learner.
 *
 * **`@3` is `@2` plus the channel rule (D31, #380):** a response whose
 * `ResponseChannel` is `upload` is never independent, whatever help was or was
 * not delivered. Every rung's kind, and `INDEPENDENCE_PRESERVING_KINDS`, are
 * exactly `@2`'s; for an `in_app` response `@3` and `@2` decide identically.
 * It is a new version rather than a note on `@2` because it changes what
 * `isIndependent: false` can mean — before it, false always meant help was
 * delivered; after it, false can also mean production was not observed — and
 * the version is what lets a reader tell the two apart. Rows that stored `@2`
 * keep `@2`: no upload existed under it, so nothing it concluded changes.
 */
export const ASSISTANCE_LADDER_VERSION = "assistance-ladder@3";

interface AssistanceRung {
  readonly summary: string;
  readonly kind: AssistanceKind;
  /** Derived from `kind`, never written by hand, so the two cannot drift. */
  readonly revealsAnswer: boolean;
  /** Derived from `kind`. True for `informative` and `revealing` since D15. */
  readonly costsIndependence: boolean;
}

/**
 * What each rung is permitted to contain, and what it does to the evidence.
 *
 * Kept as data rather than prose so that a proposed hint can be checked
 * against its declared level. The summaries describe each rung's *function*;
 * BACKEND_DESIGN §6.3 holds the Italian instantiation of them, which is
 * content, not rule.
 */
export const ASSISTANCE_LADDER: Readonly<
  Record<AssistanceLevel, AssistanceRung>
> = Object.freeze(
  Object.fromEntries(
    (
      [
        [0, "No help.", "none"],
        [
          1,
          "The task restated, repeated, or simplified. Nothing the learner did not already have.",
          "re_presentation",
        ],
        [2, "A cue drawn from the material being learned.", "informative"],
        [
          3,
          "A short gloss or keyword from outside the material being learned.",
          "informative",
        ],
        [
          4,
          "A brief explanation from outside the material being learned.",
          "informative",
        ],
        [
          5,
          "Full explanation, worked example, or the answer revealed.",
          "revealing",
        ],
      ] as readonly (readonly [AssistanceLevel, string, AssistanceKind])[]
    ).map(([level, summary, kind]) => [
      level,
      Object.freeze({
        summary,
        kind,
        revealsAnswer: kind === "revealing",
        costsIndependence: costsIndependence(kind),
      }),
    ]),
  ),
) as Readonly<Record<AssistanceLevel, AssistanceRung>>;

/**
 * The threshold at which help reveals the answer.
 *
 * Deliberately derived from ASSISTANCE_LADDER rather than written as the
 * literal 5, so that adding a rung cannot silently change what counts as
 * revealing. If a future rung 4 becomes answer-revealing, this follows.
 */
export const LOWEST_ANSWER_REVEALING_LEVEL: AssistanceLevel =
  ASSISTANCE_LEVELS.filter(
    (level) => ASSISTANCE_LADDER[level].revealsAnswer,
  ).reduce<AssistanceLevel>(
    (lowest, level) => (level < lowest ? level : lowest),
    5,
  );

/**
 * The threshold at which help costs independence. Since D15 this is rung 2,
 * one below `LOWEST_ANSWER_REVEALING_LEVEL`. The two are separate constants
 * because they answer different questions, and collapsing them is precisely
 * the confusion #67 was filed about.
 */
export const LOWEST_INDEPENDENCE_COSTING_LEVEL: AssistanceLevel =
  ASSISTANCE_LEVELS.filter(
    (level) => ASSISTANCE_LADDER[level].costsIndependence,
  ).reduce<AssistanceLevel>(
    (lowest, level) => (level < lowest ? level : lowest),
    5,
  );

export function isAssistanceLevel(value: unknown): value is AssistanceLevel {
  return (
    typeof value === "number" &&
    (ASSISTANCE_LEVELS as readonly number[]).includes(value)
  );
}

/**
 * A single piece of help shown to the learner before they responded.
 * Only help actually *delivered* counts; help that was prepared but never
 * displayed does not compromise independence.
 */
export interface DeliveredAssistance {
  readonly level: AssistanceLevel;
  /** True when this specific delivery revealed the answer. */
  readonly revealsAnswer: boolean;
  readonly deliveredAt: Date;
  /**
   * What this delivery turned out to be, when that is known to be more than
   * its rung declares — a rung-1 restatement that smuggled in a cue, say.
   * Optional: absent means the rung's declared kind stands.
   *
   * Never used to argue a delivery *down*. `effectiveKind` takes the more
   * severe of this and the rung.
   */
  readonly kind?: AssistanceKind;
}

/** The kind a delivery counts as: the most severe of everything known about it. */
function effectiveKind(entry: DeliveredAssistance): AssistanceKind {
  const candidates: AssistanceKind[] = [ASSISTANCE_LADDER[entry.level].kind];
  if (entry.kind !== undefined) candidates.push(entry.kind);
  if (entry.revealsAnswer) candidates.push("revealing");
  return candidates.reduce((worst, kind) =>
    KIND_SEVERITY[kind] > KIND_SEVERITY[worst] ? kind : worst,
  );
}

/**
 * Where a response was produced (D31, #380). An observation about the
 * response, recorded by the endpoint that received it and never by a client's
 * say-so beyond which endpoint that was (invariant 2).
 *
 * - `in_app` — produced inside DaVinci, under observation. The only channel
 *   that existed before D31; its behaviour is unchanged.
 * - `upload` — produced elsewhere and uploaded (homework). DaVinci saw the
 *   answer arrive and nothing of how it was produced (D11).
 *
 * Deliberately a required argument of `deriveIndependence` with no default: a
 * caller that forgets the channel must fail to compile, never quietly read as
 * `in_app` and manufacture independent evidence.
 */
export const RESPONSE_CHANNELS = Object.freeze(["in_app", "upload"] as const);

export type ResponseChannel = (typeof RESPONSE_CHANNELS)[number];

export function isResponseChannel(value: unknown): value is ResponseChannel {
  return (
    typeof value === "string" &&
    (RESPONSE_CHANNELS as readonly string[]).includes(value)
  );
}

/**
 * The reason code `deriveIndependence` prefixes its reason with for an upload,
 * and the `interaction_validity` reason an upload's evidence carries (level
 * `eligible`, never a timing reason — invariant 4).
 */
export const PRODUCTION_UNOBSERVED_REASON_CODE = "production_unobserved";

/**
 * Decides whether a response counts as independent.
 *
 * Independence is lost by any help that supplied information toward the
 * answer, at whatever rung it was labelled. We trust the delivery's own
 * `revealsAnswer` flag and `kind` as well as the ladder's expectation for its
 * level, because a rung-3 hint that leaked the answer must still cost
 * independence and must be reported as a reveal rather than as a cue. The
 * three are combined by severity, never averaged: this function is allowed to
 * be over-cautious and is never allowed to be over-generous.
 *
 * **The channel is decided before the list (D31).** An `upload` is never
 * independent — DaVinci did not observe it being produced — and that holds for
 * the empty list too: "no help was delivered" is a statement about DaVinci's
 * delivery log, and says nothing about help outside it. `answerWasRevealed`
 * stays derived from delivered help alone (D11), so a homework failure remains
 * a qualifying negative and can demote a status. No delivery row is ever
 * synthesised for a channel: that would store an inference in an observation's
 * shape (invariant 2).
 *
 * **It fails closed.** The test is `channel !== 'in_app'`, not
 * `channel === 'upload'`: the type makes the argument required, but an untyped
 * caller that omits it, or passes a channel nobody has defined yet, must read
 * as unobserved rather than as independent. Only observed production earns
 * independence.
 *
 * Returns the decision, its reason, and the ladder version that produced it,
 * because BACKEND_DESIGN invariant 3 requires every derived conclusion to
 * name what produced it.
 */
export function deriveIndependence(
  assistance: readonly DeliveredAssistance[],
  channel: ResponseChannel,
): {
  readonly isIndependent: boolean;
  /**
   * The **floor**, reported separately from `isIndependent` since D15.
   *
   * True when answer-revealing help preceded the response. It is not the
   * negation of `isIndependent` and must never be treated as one: between the
   * two lies every `informative` rung, where the learner was helped toward the
   * answer but not handed it.
   *
   * §8.1 reads this one rather than `isIndependent` when deciding whether a
   * *failure* counts. Being helped toward an answer and still getting it wrong
   * is, if anything, stronger evidence of not knowing — so a negative is
   * disqualified only by a reveal. Without this distinction D15 would have
   * made every status harder to demote, which `invariant-auditor` found and
   * measured: an independent success at 200 hours followed by a rung-3-helped
   * failure would have read as `retained` rather than `learning`.
   */
  readonly answerWasRevealed: boolean;
  readonly maxAssistanceLevel: AssistanceLevel;
  readonly reason: string;
  readonly ladderVersion: string;
} {
  if (channel !== "in_app") {
    const maxLevel = assistance.reduce<AssistanceLevel>(
      (max, entry) => (entry.level > max ? entry.level : max),
      0,
    );
    return {
      isIndependent: false,
      answerWasRevealed: assistance.some(
        (entry) => effectiveKind(entry) === "revealing",
      ),
      maxAssistanceLevel: maxLevel,
      reason: `${PRODUCTION_UNOBSERVED_REASON_CODE}: the work was produced outside DaVinci and uploaded; production was not observed.`,
      ladderVersion: ASSISTANCE_LADDER_VERSION,
    };
  }

  if (assistance.length === 0) {
    return {
      isIndependent: true,
      answerWasRevealed: false,
      maxAssistanceLevel: 0,
      reason: "No assistance was delivered before the response.",
      ladderVersion: ASSISTANCE_LADDER_VERSION,
    };
  }

  const maxAssistanceLevel = assistance.reduce<AssistanceLevel>(
    (max, entry) => (entry.level > max ? entry.level : max),
    0,
  );

  // Reported in severity order, not in delivery order: when an attempt held
  // both a cue and a reveal, the reason names the reveal. A reason that named
  // the cue would understate what the learner was given.
  const revealing = assistance.find(
    (entry) => effectiveKind(entry) === "revealing",
  );

  if (revealing !== undefined) {
    return {
      isIndependent: false,
      answerWasRevealed: true,
      maxAssistanceLevel,
      reason: `Answer-revealing assistance at level ${String(revealing.level)} preceded the response.`,
      ladderVersion: ASSISTANCE_LADDER_VERSION,
    };
  }

  const informative = assistance.find((entry) =>
    costsIndependence(effectiveKind(entry)),
  );

  if (informative !== undefined) {
    return {
      isIndependent: false,
      answerWasRevealed: false,
      maxAssistanceLevel,
      reason: `Assistance at level ${String(informative.level)} supplied information toward the answer.`,
      ladderVersion: ASSISTANCE_LADDER_VERSION,
    };
  }

  return {
    isIndependent: true,
    answerWasRevealed: false,
    maxAssistanceLevel,
    reason: `Assistance reached level ${String(maxAssistanceLevel)}, which only re-presented the task.`,
    ladderVersion: ASSISTANCE_LADDER_VERSION,
  };
}
